import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AIClass,
  BlueberryFlowerStageFeatures,
  CubeDimensionEnum,
  FeatureEnum,
} from '../../services/data/impl/cube/cubeSlice';
import { mapToAlias } from '../../services/data/impl/cube/cubeUtils';
import { flexCenter } from '../commonUI/cssClasses';
import { isFeatureEnum } from '../commonUtils';
import useCurrServicesAndMeasures from '../customerSummary/hooks/useCurrServicesAndMeasures';
import { orderBlueberriesByRipenessOrder } from '../scanDataApi/scanDataUtils';
import ImageLayerCheckboxWithLabel from './ImageLayerCheckboxWithLabel';
import MaskColorPicker from './MaskColorPicker';
import { selectMaskColors, setImageMaskLayerVisibility } from './imageSlice';

const MaskLayerTogglesAndColors: React.FC = () => {
  const maskColors = useAppSelector(selectMaskColors);
  const dispatch = useAppDispatch();
  const { servicesAndMeasuresWithoutFeatureFilter } =
    useCurrServicesAndMeasures();

  const currServices = useMemo(() => {
    let filteredAndSortedServices: AIClass[] = [
      ...servicesAndMeasuresWithoutFeatureFilter.availableServicesRaw,
    ];

    // Check if blueberry classes are in the list (blue was picked arbitrarily but any color would work)
    const isBlueberryPresent = filteredAndSortedServices.some(
      (el) => el[CubeDimensionEnum.className] === FeatureEnum.blueberry_Blue
    );

    const isFlowerStagesPresent = filteredAndSortedServices.some((el) =>
      BlueberryFlowerStageFeatures.includes(el[CubeDimensionEnum.className])
    );

    // if blueberry classes are in the list, sort them in ripening order
    if (isBlueberryPresent) {
      filteredAndSortedServices = orderBlueberriesByRipenessOrder({
        arrayToOrder: filteredAndSortedServices,
        blueberryKeyGetter: (a) => a[CubeDimensionEnum.className],
      });

      if (isFlowerStagesPresent) {
        // Filter out the "flower" class because each stage has its own toggle checkbox instead
        filteredAndSortedServices = filteredAndSortedServices.filter(
          (el) => el[CubeDimensionEnum.className] !== FeatureEnum.flower
        );
      }
      dispatch(
        setImageMaskLayerVisibility({
          layer: FeatureEnum.flower,
          newValue: !isFlowerStagesPresent,
        })
      );
    }
    // Filter out the "blueberry" class because each color has its own toggle checkbox instead
    filteredAndSortedServices = filteredAndSortedServices.filter(
      (el) => el[CubeDimensionEnum.className] !== FeatureEnum.blueberry
    );
    // Generate a set of classNames to remove duplicates and alias the names (so "inflorescence" becomes "clusters" for example)
    const classNames = new Set<FeatureEnum>();
    filteredAndSortedServices.forEach((el) => {
      if (isBlueberryPresent) {
        // If it's a blueberry class, add it to the set without aliasing bc all the colors alias to "blueberry"
        classNames.add(el[CubeDimensionEnum.className]);
      } else {
        // If it's not a blueberry class, alias it and add it to the set
        classNames.add(mapToAlias(el[CubeDimensionEnum.className]));
      }
    });

    // Return the list of classNames
    return Array.from(classNames);
  }, [servicesAndMeasuresWithoutFeatureFilter.availableServicesRaw]);

  const maskLayerControls = currServices.map((service) => {
    const isKnownModel = isFeatureEnum(service);

    if (isKnownModel) {
      return (
        <Box key={service} sx={{ display: 'flex', alignItems: 'center' }}>
          <MaskColorPicker maskColor={maskColors[service]} feature={service} />
          <ImageLayerCheckboxWithLabel layer={service} />
        </Box>
      );
    } else {
      console.log(`Unknown model image mask detected: ${service}`);
      return null;
    }
  });

  return (
    <Box sx={{ ...flexCenter, flexDirection: 'column', pb: '1rem' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {maskLayerControls}
      </Box>
    </Box>
  );
};

export default MaskLayerTogglesAndColors;
